import ecoPlatformLogo from 'assets/images/eco-epd-logo.png';
import { EPDProductClassificationId } from 'components/v2/compiler/constants';
import epdInternationalLogo from 'images/EPD-logo.svg';
import fallbackImage from 'assets/images/image-placeholder.svg';
import React from 'react';
import FileService from 'services/FileService';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { CustomLogotypeId } from 'types/CustomLogotypeId';
import {
  EPDCoverPageModel,
  EPDDefinitionModel,
  EPDOrganizationInformationModel,
  EPDProductInformationModel,
  ProductStatusEnum,
} from 'types/types';
import DebugOnly from 'util/dev-tools/DebugOnly';
import RenderCounter from 'util/dev-tools/RenderCounter';
import { formatDate } from 'util/utils';

import {
  BottomText,
  Container,
  DeclarationAccordance,
  DeclarationHeader,
  DeclarationName,
  GreenLeftLine,
  LeftSubsection,
  LeftSubsectionInnerContainer,
  PdfCoverPage,
  ProductsIncluded,
  ProductsIncludedHeader,
  RightSubsection,
  RightSubsectionCaption,
  RightSubsectionText,
  SecondSection,
  ThirdSection,
  TopSection,
} from './Styles.styled';
import ImageWithFallback from 'components/error-fallback/ImageWithFallback';
import usePcrV2 from 'components/v2/compiler/hooks/usePcrV2';

const CoverPage: React.FunctionComponent<{
  epdDefinitionInformation: EPDDefinitionModel;
  coverPageInformation: EPDCoverPageModel;
  products: EPDProductInformationModel[];
  getOptionsFromDictionary: any;
  organizationInformation?: EPDOrganizationInformationModel;
  verificationInfo?: any;
  qrImageUrl: any;
}> = ({
  epdDefinitionInformation,
  coverPageInformation,
  products,
  getOptionsFromDictionary,
  organizationInformation,
  verificationInfo,
  qrImageUrl,
}) => {
  const isPcrV2 = usePcrV2();
  const epdClassificationOptions = getOptionsFromDictionary(
    isPcrV2 ? EPD_DICTIONARY_CODES.EPD_CLASSIFICATION_V2 : EPD_DICTIONARY_CODES.EPD_CLASSIFICATION
  );
  const epdClassification = epdClassificationOptions.find((x: any) => x.value == epdDefinitionInformation.epdClassification);
  const productsInEPD =
    products.length <= 10
      ? products?.map((x) => x.productName).join(', ')
      : 'The EPD covers more than 10 products. Please refer to the product information section for the list of products.';
  const allProductsOnTheMarket = products.every(
    (p) => p.productStatus === ProductStatusEnum.OnTheMarket || !p.productStatus
  );
  const showDisclaimerStatusForMultipleProducts =
    isPcrV2 &&
    !allProductsOnTheMarket &&
    epdDefinitionInformation.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID;
  const showDisclaimerStatusForSingleProduct =
    isPcrV2 &&
    !allProductsOnTheMarket &&
    epdDefinitionInformation.epdClassification == EPDProductClassificationId.SINGLE_PRODUCT_ID;
  const conformityStandardsOptions = getOptionsFromDictionary(EPD_DICTIONARY_CODES.CONFORMITY_STANDARDS);
  function getSelectedStandards(declaredStandards: string | undefined, conformityStandardsOptions: any[]) {
    if (!declaredStandards) return [];

    const standardsArray = declaredStandards?.split(',');

    return standardsArray?.map((x: any) => conformityStandardsOptions.find((y) => y.value == x));
  }

  const inAccordanceWithStandards = getSelectedStandards(
    epdDefinitionInformation?.declaredStandards,
    conformityStandardsOptions
  )
    .map((x) => x.label)
    .join(', ');

  const licensee = organizationInformation?.licenseeName;
  const collocationCenter = organizationInformation?.collocationCenterName;
  const companyLogotypeImage = FileService.getImageUrl(coverPageInformation?.companyLogoImages![0]?.id);

  const getLogotypeSrc = () => {
    if (!licensee && !collocationCenter) {
      return epdInternationalLogo;
    }

    if (licensee && !collocationCenter) {
      return organizationInformation?.licenseeLogotypeUrl;
    }

    if (!licensee && collocationCenter) {
      return organizationInformation?.collocationCenterLogotypeUrl;
    }

    // licensee && collocationCenter
    switch (epdDefinitionInformation.customLogotype) {
      case CustomLogotypeId.COLLOCATION_CENTER_LOGOTYPE_ID:
        return organizationInformation?.collocationCenterLogotypeUrl;
      case CustomLogotypeId.LICENSEE_LOGOTYPE_ID:
        return organizationInformation?.licenseeLogotypeUrl;
      default:
        return fallbackImage;
    }
  };

  return (
    <PdfCoverPage className="cover">
      <DebugOnly>
        <RenderCounter componentName="cover page" threshold={3} />
      </DebugOnly>
      <GreenLeftLine />
      <Container>
        <TopSection>
          <LeftSubsection>
            <LeftSubsectionInnerContainer>
              <div>
                <div style={{ padding: '1cm 0 1.5cm 0' }}>
                  <DeclarationHeader>
                    Environmental <br />
                    product <br />
                    declaration
                  </DeclarationHeader>
                </div>
                <div style={{ paddingBottom: '0.5cm' }}>
                  <DeclarationName>{epdDefinitionInformation.declarationName}</DeclarationName>
                  <DeclarationAccordance>In accordance with: {inAccordanceWithStandards}</DeclarationAccordance>
                </div>
                <div>
                  <ProductsIncludedHeader>Products included in the EPD:</ProductsIncludedHeader>
                  <ProductsIncluded>{productsInEPD}</ProductsIncluded>
                  {showDisclaimerStatusForSingleProduct &&
                    (products[0].productStatus === ProductStatusEnum.ForthcomingOrNotYetOnTheMarket ? (
                      <ProductsIncluded>
                        Product not yet on the market - Results of this EPD shall be used with care as the LCI data is not
                        yet based on 1 year of production which may result in increased uncertainty.
                      </ProductsIncluded>
                    ) : (
                      <ProductsIncluded>
                        Product recently on the market - Results of this EPD shall be used with care as the LCI data is not
                        yet based on 1 year of production which may result in increased uncertainty.
                      </ProductsIncluded>
                    ))}
                  {showDisclaimerStatusForMultipleProducts && (
                    <ProductsIncluded>
                      One or more products are not yet or recently on the market. Please refer to the product information
                      section for the market status of each product - Results of this EPD shall be used with care as the LCI
                      data is not yet based on 1 year of production which may result in increased uncertainty.
                    </ProductsIncluded>
                  )}
                </div>
              </div>

              <div>
                {products?.some((x: EPDProductInformationModel) => x.isTheProjectInThePlanningOrDesignPhase === true) && (
                  <BottomText>Project in planning/design phase</BottomText>
                )}
                <BottomText>
                  An EPD may be updated or depublished if conditions change. To find the latest version of the EPD and to
                  confirm its validity, see www.environdec.com
                </BottomText>
                {!!epdClassification && <BottomText>{epdClassification.label}</BottomText>}
                {[
                  EPDProductClassificationId.SECTOR_OF_MULTIPLE_PRODUCTS_ID,
                  EPDProductClassificationId.SECTOR_OF_AVERAGE_PRODUCTS_ID,
                  EPDProductClassificationId.SECTOR_OF_WORST_CASE_PRODUCTS_ID,
                ].includes(epdDefinitionInformation.epdClassification!) && (
                  <BottomText>{epdDefinitionInformation.documentCoverageStatement}</BottomText>
                )}
              </div>
            </LeftSubsectionInnerContainer>
          </LeftSubsection>
          <RightSubsection>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
              <div>
                <RightSubsectionCaption>EPD owner</RightSubsectionCaption>
                <RightSubsectionText>{organizationInformation?.companyName}</RightSubsectionText>

                <RightSubsectionCaption>Programme</RightSubsectionCaption>
                <RightSubsectionText>
                  International EPD System
                  <br />
                  www.environdec.com
                </RightSubsectionText>

                <RightSubsectionCaption>Programme operator</RightSubsectionCaption>
                <RightSubsectionText>EPD International AB</RightSubsectionText>
                {licensee && (
                  <>
                    <RightSubsectionCaption>Licensee</RightSubsectionCaption>
                    <RightSubsectionText>{licensee}</RightSubsectionText>
                  </>
                )}

                <RightSubsectionCaption>Registration number</RightSubsectionCaption>
                <RightSubsectionText>{epdDefinitionInformation.fullIdentificationNumber}</RightSubsectionText>

                <RightSubsectionCaption>Version date</RightSubsectionCaption>
                <RightSubsectionText>{formatDate(verificationInfo?.approvalDate)}</RightSubsectionText>

                <RightSubsectionCaption>Validity date</RightSubsectionCaption>
                <RightSubsectionText>{formatDate(verificationInfo?.validityDate)}</RightSubsectionText>
              </div>
              <div style={{ display: 'flex', alignContent: 'flex-end', justifyContent: 'flex-end' }}>
                <img style={{ maxWidth: '75px', aspectRatio: 0.75 }} src={qrImageUrl} alt="QR code" />
              </div>
            </div>
          </RightSubsection>
        </TopSection>
        <SecondSection>
          <img
            height={'100%'}
            width={'100%'}
            src={FileService.getImageUrl(coverPageInformation?.coverPageProductImage?.id)}
            alt="Cover page image"
          />
        </SecondSection>
        <ThirdSection>
          <div>
            <ImageWithFallback
              src={getLogotypeSrc()}
              fallback={fallbackImage}
              alt="EPD Logotype"
              height={40}
              style={{ maxHeight: '40px' }}
            />
          </div>
          <div>
            <img height={40} style={{ maxHeight: '40px' }} src={ecoPlatformLogo} alt="ECO Platform logotype" />
          </div>
          <div>
            <ImageWithFallback
              src={companyLogotypeImage}
              fallback={fallbackImage}
              alt="Organization logotype"
              height={40}
              style={{ maxHeight: '40px' }}
            />
          </div>
        </ThirdSection>
      </Container>
    </PdfCoverPage>
  );
};

export default CoverPage;
